import { useMobileScrollReveal } from "@/hooks/useMobileScrollReveal";
import { useId, useRef, useState, type KeyboardEvent } from 'react';
import { Activity, CalendarDays, Check, FlaskConical, Leaf, Moon, type LucideIcon } from 'lucide-react';
import './product-narrative.css';

export type FormulaKey = 'mag' | 'liver' | 'ova';
export type Concern = { id: string; label: string; icon: LucideIcon; image: string; alt: string; heading: string; points: string[] };
export type FormulaStory = {
  handle: 'mag-absorb-pro' | 'liver-biome-plus' | 'ova-balance'; title: string;
  concernTitle: string; concernIntro: string; concerns: Concern[];
  panelTitle: string; supportTitle: string; support: string[];
  ingredients: string[]; packCount: string; note: string;
};

/** Product names, ingredient names and pack counts reflect the current TruWe pack art and
 * existing site positioning. Exact doses stay in the authoritative formula component below. */
export function getFormulaStory(key: FormulaKey): FormulaStory {
  const root = '/images/truwe-lifestyle/';
  const formulation = (handle: string): Concern => ({
    id: 'formula', label: 'Your formula', icon: FlaskConical,
    image: `/images/truwe-products/${handle}.webp`,
    alt: 'Original TruWe retail packaging',
    heading: 'Know what you are choosing.',
    points: [
      'Read the ingredient forms and the amount per serving on the complete label.',
      'Choose with a clear view of the pack contents, directions and suitability.',
    ],
  });
  const note = 'Follow the directions on your product label. Individual needs and experiences vary.';

  if (key === 'mag') return {
    note, handle: 'mag-absorb-pro', title: 'Mag Absorb Pro',
    concernTitle: 'Busy days. A better wind-down starts here.',
    concernIntro: 'Explore the everyday moments behind your routine, then take a closer look at the formula.',
    concerns: [
      { id: 'evening', label: 'Busy evenings', icon: Moon, image: root + 'mag-evening.webp', alt: 'Illustrative evening scene with an adult reading in bed', heading: 'Make space to switch off.', points: ['Is your evening the first quiet moment in a full day? A familiar wind-down routine can give it some structure.', 'Mag Absorb Pro is positioned for sleep, recovery and muscle support. Explore its ingredients before you choose.'] },
      { id: 'active', label: 'Active days', icon: Activity, image: root + 'mag-recovery.webp', alt: 'Illustrative scene of an adult stretching at home', heading: 'Give recovery a place in your routine.', points: ['Training, movement and rest all deserve space in your week.', 'Start with the full formula and product directions when deciding whether magnesium fits your needs.'] },
      formulation('mag-absorb-pro'),
    ],
    panelTitle: '5 magnesium forms. One formula.',
    supportTitle: 'Sleep, recovery & muscle support',
    support: ['Sleep & relaxation', 'Muscle recovery & function', 'Nerve & heart support'],
    ingredients: ['Magnesium bisglycinate', 'Aquamin® marine magnesium', 'Magnesium chloride', 'Magnesium aspartate', 'Magnesium gluconate'],
    packCount: '60 tablets',
  };

  if (key === 'liver') return {
    note, handle: 'liver-biome-plus', title: 'Liver Biome Plus',
    concernTitle: 'Your everyday habits deserve daily attention.',
    concernIntro: 'Meals, movement and informed choices. Explore the routine around your supplement.',
    concerns: [
      { id: 'meals', label: 'Everyday meals', icon: Leaf, image: root + 'liver-meal.webp', alt: 'Illustrative scene of adults preparing a balanced meal', heading: 'Begin with the habits you repeat.', points: ['Busy schedules can make it harder to give mealtimes your attention.', 'Liver Biome Plus is positioned for liver and gut support, alongside your everyday routine.'] },
      { id: 'movement', label: 'Daily movement', icon: Activity, image: root + 'liver-walk.webp', alt: 'Illustrative scene of adults walking outside', heading: 'Small moments. A consistent routine.', points: ['A walk can be a simple way to make time for yourself during the day.', 'A supplement choice belongs within a wider routine. Explore the formula and label directions.'] },
      formulation('liver-biome-plus'),
    ],
    panelTitle: 'Get to know the core ingredients.',
    supportTitle: 'Liver & gut support',
    support: ['600 MG OF NAC STUDY LEVEL DOSE', 'Silymarin, curcumin & alpha-lipoic acid', 'A probiotic consortium'],
    ingredients: ['N-Acetyl Cysteine (NAC)', 'Liposomal Milk thistle / silymarin', 'Liposomal Alpha-lipoic acid', 'Liposomal Curcumin', 'Liver Supporting Probiotic consortium'],
    packCount: '60 tablets',
  };

  return {
    note, handle: 'ova-balance', title: 'Ova Balance',
    concernTitle: 'A routine with room for you.',
    concernIntro: 'Make space for your wellbeing, and understand your formula before you choose.',
    concerns: [
      { id: 'routine', label: 'Your routine', icon: CalendarDays, image: root + 'ova-journal.webp', alt: 'Illustrative scene of an adult writing in a journal', heading: 'Pay attention to your own patterns.', points: ['A journal can give you a place to note your routine and the questions you want to ask.', 'Ova Balance is positioned for hormonal harmony support. Check the ingredients and discuss suitability when needed.'] },
      { id: 'habits', label: 'Daily habits', icon: Leaf, image: root + 'ova-water.webp', alt: 'Illustrative scene of hands beside a glass of clear water; not product preparation', heading: 'Make consistency feel practical.', points: ['Give your everyday habits a place in your schedule.', 'For preparation and dosage, use the actual product directions. This image illustrates a daily moment.'] },
      formulation('ova-balance'),
    ],
    panelTitle: 'A closer look at the inositol formula.',
    supportTitle: 'Hormonal harmony support',
    support: ['Hormonal balance support', 'Metabolic health support', 'Cycle regularity support'],
    ingredients: ['Myo + D-chiro inositol 40:1', 'Berberine HCl', 'L-5-MTHF folate', 'Vitamin D3 600 IU (15 mcg)', 'Magnesium bisglycinate', 'Chromium picolinate'],
    packCount: '30 sachets',
  };
}

export function ConcernExplorer({ story, tone }: { story: FormulaStory; tone: FormulaKey }) {
  const headingReveal = useMobileScrollReveal<HTMLElement>();
  const imageReveal = useMobileScrollReveal<HTMLElement>("media");
  const copyReveal = useMobileScrollReveal<HTMLDivElement>();
  const [selected, setSelected] = useState(0);
  const id = useId();
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  const current = story.concerns[selected] ?? story.concerns[0];
  if (!current) return null;
  const select = (i: number) => { setSelected(i); buttons.current[i]?.focus(); };
  const onKeys = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    let next = i;
    if (e.key === 'ArrowRight') next = (i + 1) % story.concerns.length;
    else if (e.key === 'ArrowLeft') next = (i + story.concerns.length - 1) % story.concerns.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = story.concerns.length - 1;
    else return;
    e.preventDefault(); select(next);
  };
  return <section className="twn twn-concerns" data-tone={tone} aria-labelledby={`${id}-heading`}>
    <header ref={headingReveal} className="twn-intro">
      <span className="twn-pill">Your everyday</span>
      <h2 id={`${id}-heading`}>{story.concernTitle}</h2>
      <p>{story.concernIntro}</p>
    </header>
    <div className="twn-tabs" role="tablist" aria-label="Explore your routine">
      {story.concerns.map((item, i) => {
        const Icon = item.icon;
        return <button type="button" role="tab" key={item.id} id={`${id}-tab-${i}`}
          aria-selected={i === selected} aria-controls={`${id}-panel`} tabIndex={i === selected ? 0 : -1}
          ref={el => { buttons.current[i] = el; }} onClick={() => setSelected(i)} onKeyDown={e => onKeys(e, i)}>
          <span className="twn-tab-icon"><Icon size={25} strokeWidth={1.5} aria-hidden="true" /></span>{item.label}
        </button>;
      })}
    </div>
    <div className="twn-concern-panel" role="tabpanel" id={`${id}-panel`} aria-labelledby={`${id}-tab-${selected}`} tabIndex={0}>
      <figure ref={imageReveal}>
        <img key={current.image} src={current.image} alt={current.alt} width={1400} height={965} loading="lazy" decoding="async" data-pack={current.id === 'formula'} />
        {current.id !== 'formula' && <figcaption>Illustrative scene</figcaption>}
      </figure>
      <div ref={copyReveal} className="twn-explanation">
        <h3>{current.heading}</h3>
        <ul>{current.points.map(point => <li key={point}><Check size={18} aria-hidden="true" /><p>{point}</p></li>)}</ul>
      </div>
    </div>
  </section>;
}

export function ProductNarrativeSections({ productKey }: { productKey: FormulaKey }) {
  const story = getFormulaStory(productKey);
  return <div className="twn-shell">
    <ConcernExplorer key={productKey} story={story} tone={productKey} />
  </div>;
}
